import { palm, snowflake, sprite, star, swirl, waves, figure, groundRing, shield, crack } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';

/**
 * ฝ่ามือเมฆาคล้อย (หุบเขาเมฆา, soft): open palms in drifting valley cloud. Heaped white cloud with a cool
 * blue-grey underside, chill flecks for the slowing moves, no harsh colours.
 */

const CLOUD = '#f2f8fb';
const CLOUD_SH = '#a9c6da';
const MIST = '#7fb6d6';
const CHILL = '#d8f2ff';
const SKIN = C.skin;

/** A heaped cloud: a cool shadow bank under a white top. */
function bank(p: Pix, x: number, y: number, w: number): void {
  const n = Math.max(2, Math.round(w / 6));
  p.rect(x + 1, y - 2, w - 2, 3, CLOUD_SH);
  for (let k = 0; k < n; k++) {
    const cx = x + 3 + (k * (w - 6)) / (n - 1);
    const r = k % 2 ? 3.2 : 2.6;
    p.ell(cx, y - 2, r, r * 0.85, CLOUD_SH);
    p.ell(cx - 0.6, y - 2.6, r - 1, (r - 1) * 0.8, CLOUD);
  }
}

/** A running figure twice the kit size (14×18). */
function bigFigure(p: Pix, x: number, y: number, col: string, flip = false): void {
  sprite(p, x, y, ['....hh.', '....hh.', 'b.bbbb.', '.b.bb.b', '...bb..', '..bbb..', '.b...bb', 'b......'], { h: col, b: col }, { k: 2, flip });
}

registerIcons('skill', {
  // A close palm strike, cloud rolling off it in waves.
  'cp-wave': (p) => {
    waves(p, 15, 16, 5, 4, MIST, 0.95, 4);
    palm(p, 3, 8, SKIN, { lines: C.skinDark });
    bank(p, 1, 26, 13);
  },
  // The guard-stripping grab: a hand closes on a guard and pulls it loose, cloud spilling off the rim.
  'cp-seize': (p) => {
    shield(p, 12, 12, 14, 15, C.steelMid, C.steel);
    crack(p, [
      [12, 13],
      [10, 18],
      [13, 21],
      [11, 26],
    ], C.steelDark);
    palm(p, 13, 1, SKIN, { flip: true, flipY: true });
    bank(p, 1, 9, 9);
    bank(p, 20, 18, 9);
  },
  // Spinning palms in a cloud whirl.
  'cp-storm': (p) => {
    swirl(p, 16, 16, 14, CLOUD_SH, { arms: 3, turn: 4.6 });
    swirl(p, 16, 16, 11, CLOUD, { arms: 3, turn: 4.6 });
    palm(p, 10, 9, SKIN, { lines: C.skinDark });
  },
  // Cloud breath: a slow curl rising from a cloud bank, one bright breath-bead.
  'cp-breath': (p) => {
    bank(p, 5, 27, 22);
    p.path([
      [16, 23],
      [19, 18],
      [15, 14],
      [19, 9],
      [23, 11],
    ], CLOUD_SH, 3);
    p.path([
      [16, 23],
      [19, 18],
      [15, 14],
      [19, 9],
      [23, 11],
    ], CLOUD, 1);
    p.ell(24, 6, 2.5, 2.5, CHILL);
    p.px(25, 5, C.white);
  },
  // Light as cloud: a figure leaping off a cloud, speed lines behind.
  'cp-drift': (p) => {
    bank(p, 7, 28, 22);
    bigFigure(p, 13, 6, CLOUD);
    for (const [y, l] of [
      [11, 6],
      [15, 8],
      [19, 5],
    ] as const)
      p.rect(11 - l, y, l, 1, MIST);
  },
  // A far palm: a long cloud blast that chills what it reaches.
  'cp-roar': (p) => {
    for (const [x, r] of [
      [14, 2],
      [18, 2.6],
      [22, 3.2],
      [26, 3.8],
    ] as const) {
      p.ell(x, 16, r, r, CLOUD_SH);
      p.ell(x - 0.5, 15.4, r - 0.9, r - 0.9, CLOUD);
    }
    palm(p, 1, 9, SKIN, { lines: C.skinDark });
    snowflake(p, 26, 7, 3, CHILL);
    snowflake(p, 21, 25, 2, CHILL, false);
  },
  // A chosen ring of cold cloud: a trapped figure inside.
  'cp-ring': (p) => {
    groundRing(p, 16, 23, 13, 5, MIST);
    bank(p, 4, 28, 9);
    bank(p, 19, 28, 9);
    figure(p, 13, 14, C.paper, 'stand');
    snowflake(p, 6, 8, 3, CHILL);
    snowflake(p, 26, 6, 2, CHILL, false);
  },
  // The cloud palm that covers the sky: a huge palm over a ring of cloud, stars of a daze.
  'cp-heaven': (p) => {
    palm(p, 3, 1, SKIN, { k: 2 });
    for (const i of [8, 14, 20]) p.rect(3 + i, 7, 2, 7, C.skinDark);
    bank(p, 3, 28, 26);
    star(p, 27, 8, 2, CHILL);
    star(p, 5, 6, 2, CHILL);
  },
});
